import {isStandalone, isTestEnv, logger} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {AmplitudeUserProps, TrackingEvent} from '@common/tracking-events'
import * as YougileAnalytics from './yougile-analytics'

export {TrackingEvent}

declare global {
  interface Window {
    amplitudeGroups: {[groupName: string]: string}
  }
}

// check correctness of TrackingEvent enum and report inconsistency
// todo: move to tests
Object.keys(TrackingEvent).forEach((k: keyof typeof TrackingEvent) => {
  const val = TrackingEvent[k]
  const keyFromVal = (val[0].toLowerCase() + val.slice(1)).replace(':', '')
  if (keyFromVal !== k) {
    logger.error('Incorrect Tracking event object', k, val)
  }
})

const formatName = (name: string): string =>
  name
    .replaceAll('_', ' ')
    .replace(/([A-Z])/g, (_, letter) => ' ' + letter)
    .trim()

/**
 * Allows to track user events and send usage statistics.
 */
export class Tracking {
  private static enabled = false
  private static timeout = 2000
  private static verbose = false
  private static initialized = false
  private static throttledEvents: Map<TrackingEvent, number> = new Map()

  public static setVerbose(val: boolean): void {
    Tracking.verbose = val
  }

  public static enable(): void {
    Tracking.enabled = true
  }

  public static disable(): void {
    Tracking.enabled = false
  }

  public static isActive(): boolean {
    return Tracking.enabled && !isStandalone()
  }

  public static identify(
    userId?: DataObjectId,
    props?: AmplitudeUserProps
  ): void {
    userId && YougileAnalytics.setPersonProperties(userId, props)
  }

  public static send(
    event: TrackingEvent,
    eventProps?: {[key: string]: unknown}
  ): void {
    if (isTestEnv()) {
      if (eventProps) {
        logger.log('Track, send event', event, eventProps)
      } else {
        logger.log('Track, send event', event)
      }
    }
    if (isStandalone()) {
      return
    }
    const parts = event.split(':')
    if (Tracking.verbose) {
      logger.log('Event', ...parts)
    }
    return Tracking.sendBase(event, eventProps)
  }

  public static sendThrottled(
    event: TrackingEvent,
    eventProps?: {[key: string]: unknown},
    throttleTime = 10_000
  ): void {
    if (Tracking.throttledEvents.has(event)) {
      const lastSent = Tracking.throttledEvents.get(event)
      if (lastSent && Date.now() - lastSent < throttleTime) {
        // event is throttled, do not send
        return
      }
    }
    Tracking.throttledEvents.set(event, Date.now())
    if (isTestEnv()) {
      if (eventProps) {
        logger.log('Track, send throttled event', event, eventProps)
      } else {
        logger.log('Track, send throttled event', event)
      }
    }
    if (isStandalone()) {
      return
    }
    const parts = event.split(':')
    if (Tracking.verbose) {
      logger.log('Throttled Event', ...parts)
    }
    return Tracking.sendBase(event, eventProps)
  }

  private static sendBase(
    event: TrackingEvent,
    eventProps?: {
      [key: string]: unknown
    }
  ): void {
    if (!Tracking.enabled) {
      return
    }

    if (!this.initialized) {
      logger.log(
        'trying to send event to yougile analytics but its not initialized'
      )
    }

    const [category, name] = event.split(':')

    YougileAnalytics.capture(
      `${formatName(name)}:${formatName(category)}`,
      eventProps
    )
  }

  static initialize(cb?: VoidFunction): void {
    if (Tracking.initialized) {
      logger.warn('Yougile analytics already initialised. Skip second attempt')
      return
    }

    YougileAnalytics.init()

    cb?.()

    Tracking.initialized = true
  }

  /**
   * Set some property for user in amplitude
   * for example, user lang, or theme
   */
  static sendUserProperty(
    props: AmplitudeUserProps,
    sendOnce?: boolean
  ): Promise<void> {
    if (isTestEnv()) {
      if (sendOnce) {
        logger.log('Track, send user prop', props, 'sendOnce', sendOnce)
      } else {
        logger.log('Track, send user prop', props)
      }
    }

    if (!Tracking.enabled) {
      return Promise.resolve()
    }
    const promise = YougileAnalytics.setPersonProperties(undefined, props)

    const timeoutPromise = new Promise<void>(resolve => {
      setTimeout(() => resolve(), Tracking.timeout)
    })

    return Promise.race([promise, timeoutPromise])
  }
}
